import { Layers2, ListMusic } from "lucide-react";

import { Badge, EmptyState, cn } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import { formatTrackDuration } from "@/music-catalog/utils/catalog-view";

import type { LibraryProject } from "@/cloud/types/types";

/**
 * The project's tracklist, set as a list of tracks rather than as a table of
 * rows.
 *
 * No card and no rules between the tracks. A card per row put a border and a
 * shadow around every track, which at seventeen of them reads as seventeen
 * objects instead of one album; one card around the whole thing is better but
 * still draws a box that says nothing, and a hairline between every pair is the
 * same clutter at a lighter weight. A tracklist is already a list — the numbers
 * down its left edge are all the structure it needs, and the row a pointer is
 * over is the only one that has to be marked out.
 *
 * Every track carries its artist, including where it repeats the one above.
 * Hiding the repeats reads as missing data rather than as tidiness: a tracklist
 * where nine rows name an artist and eighteen do not looks like eighteen tracks
 * whose artist was never filled in, which is a real state this catalog can be in
 * and must not be imitated by a layout rule.
 */
export const ProjectTracklist = ({
  duplicateEntryIds,
  project,
}: {
  duplicateEntryIds: Set<string>;
  project: LibraryProject;
}) => {
  const { t } = useTranslation();

  if (project.tracks.length === 0) {
    return (
      <EmptyState
        icon={<ListMusic />}
        title={t("project.noTracks.title")}
        description={t("project.noTracks.description")}
      />
    );
  }

  return (
    <ol className="flex flex-col">
      {project.tracks.map((track) => {
        const duration = formatTrackDuration(track.durationSeconds);
        const isDuplicate = duplicateEntryIds.has(`${project.desktopProjectId}:${track.id}`);

        return (
          <li
            key={track.id}
            className="group flex items-center gap-4 rounded-md px-3 py-2 transition-colors hover:bg-accent/40"
          >
            {/*
              The track's own place in its tracklist, as the project recorded it
              — never this row's index, which would renumber a tracklist rather
              than report it. A plain figure rather than a filled plate: on a
              page about one album these are a column to read down, and a tile
              behind each is seventeen small objects competing with the titles.
            */}
            <span className="w-7 shrink-0 text-right font-display text-sm text-muted-foreground/60 tabular-nums transition-colors group-hover:text-muted-foreground">
              {track.position}
            </span>

            <div className="flex min-w-0 flex-1 flex-col">
              {/*
                A track's own words, never translated and never tidied: this is
                the user's data, and an empty title is a fact about their catalog
                rather than something to fill in.
              */}
              <p className="truncate text-sm font-medium">{track.title}</p>
              <p className="truncate text-xs text-muted-foreground">{track.artist}</p>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              {isDuplicate && (
                <Badge tone="warning">
                  <Layers2 className="size-3" aria-hidden />
                  {t("catalog.duplicate")}
                </Badge>
              )}
              {/*
                Absent rather than "0:00" when the desktop never read a duration:
                a zero is a claim about the track and no reading is not one. The
                column keeps its width either way, so one unmeasured track does
                not knock the rest of the list out of alignment.
              */}
              <span
                className={cn(
                  "w-10 text-right text-sm tabular-nums",
                  duration === undefined ? "text-muted-foreground/40" : "text-muted-foreground",
                )}
              >
                {duration ?? "—"}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
};
